/** Exact Binance-style rejection copy for leverage / quantity limits. */
export const FUTURES_LEVERAGE_REJECTION_MSG =
    "Order rejected due to exceeding the maximum allowable quantity at current leverage level. To close position, please cancel the open orders and try again later.";

export function isFuturesLeverageRejectionMessage(msg) {
    const s = String(msg ?? "").toLowerCase();
    return (
        s.includes("maximum allowable quantity") ||
        s.includes("exceeding the maximum") ||
        s.includes("leverage level") ||
        (s.includes("position size") && s.includes("leverage"))
    );
}

/** Unwrap MongoDB $numberDecimal or plain string/number to a finite number. */
export function decUsd(v) {
    if (v == null) return null;
    if (typeof v === "object" && v !== null && "$numberDecimal" in v) {
        const n = Number(v.$numberDecimal);
        return Number.isFinite(n) ? n : null;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

/**
 * Largest USD notional cap among tiers that support `selectedLeverage`.
 * Tiers with max_notional_usd: null are treated as uncapped (Infinity).
 */
export function getMaxNotionalAtLeverage(tiers, selectedLeverage) {
    if (!Array.isArray(tiers) || tiers.length === 0) return Infinity;
    const lev = Number(selectedLeverage);
    if (!Number.isFinite(lev) || lev < 1) return Infinity;

    let maxNotional = 0;
    let hasFinite = false;
    for (const tier of tiers) {
        if (Number(tier.max_leverage) >= lev) {
            const parsed = decUsd(tier.max_notional_usd);
            const cap = parsed == null ? Infinity : parsed;
            if (Number.isFinite(cap)) hasFinite = true;
            if (cap > maxNotional) maxNotional = cap;
        }
    }
    if (!hasFinite && maxNotional === 0) return Infinity;
    return maxNotional;
}

/** Unique tier max_leverage values for quick-pick buttons, ascending (e.g. 5, 10, 20, 50, 100, 125). */
export function getTierLeverageButtons(leverageTiers, maxLeverage = 125) {
    const maxLev = Number(maxLeverage) || 125;
    if (!Array.isArray(leverageTiers) || leverageTiers.length === 0) {
        return [1, maxLev].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b);
    }
    const set = new Set();
    for (const tier of leverageTiers) {
        const ml = Number(tier.max_leverage);
        if (Number.isFinite(ml) && ml >= 1) set.add(Math.round(ml));
    }
    const arr = [...set].sort((a, b) => a - b);
    return arr.length ? arr : [1, maxLev];
}

/** Max base-asset quantity at a leverage for a given price. */
export function getMaxQuantityAtLeverage(leverageTiers, selectedLeverage, price) {
    const maxNotional = getMaxNotionalAtLeverage(leverageTiers, selectedLeverage);
    const p = Number(price);
    if (!Number.isFinite(p) || p <= 0 || maxNotional === Infinity) return null;
    return maxNotional / p;
}

function stepDecimals(stepSize) {
    const s = String(stepSize);
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : s.length - dot - 1;
}

/** Round quantity down to the nearest valid step_size multiple. */
export function roundQtyDownToStep(qty, stepSize) {
    const q = Number(qty);
    const step = Number(stepSize);
    if (!Number.isFinite(q) || q <= 0) return 0;
    if (!Number.isFinite(step) || step <= 0) return q;
    const prec = stepDecimals(step);
    const steps = Math.floor(q / step + 1e-12);
    return parseFloat((steps * step).toFixed(prec));
}

/**
 * Socket taker_fee_rate is a percent (0.058 → 0.058%).
 * Returns the decimal fraction used in margin math (0.00058).
 */
export function resolveTakerFeeRate(contract) {
    if (!contract) return 0;
    const rate = Number(contract.taker_fee_rate);
    if (Number.isFinite(rate) && rate >= 0) return rate / 100;
    const pct = Number(contract.taker_fee);
    if (Number.isFinite(pct) && pct >= 0) return pct / 100;
    return 0;
}

/**
 * Margin required to open a position: notional × (1/leverage + decimal taker fee).
 * takerFeeRate is a fraction (0.00058), not the socket percent (0.058).
 * Inverse of computeMaxOpenNotional — same formula used for Max sizing.
 */
export function computeFuturesOrderMargin(notional, leverage, takerFeeRate = 0) {
    const n = Number(notional);
    const lev = Math.max(1, Number(leverage) || 1);
    const fee = Number(takerFeeRate);
    if (!Number.isFinite(n) || n <= 0) return 0;
    const feeVal = Number.isFinite(fee) && fee >= 0 ? fee : 0;
    return n * (1 / lev + feeVal);
}

/**
 * Max position notional (USDT) from socket effective_available:
 *   maxNotionalUsdt = avail / (1/leverage + decimal taker fee)
 * takerFeeRate is a fraction (0.00058 for a 0.058% socket rate).
 */
export function computeMaxOpenNotional(effectiveAvailable, leverage, takerFeeRate) {
    const avail = Number(effectiveAvailable);
    const lev = Math.max(1, Number(leverage) || 1);
    const fee = Number(takerFeeRate);
    if (!Number.isFinite(avail) || avail <= 0) return 0;
    const feeVal = Number.isFinite(fee) && fee >= 0 ? fee : 0;
    const denom = 1 / lev + feeVal;
    if (denom <= 0) return 0;
    return avail / denom;
}

/**
 * Max qty in base asset from notional and contract mark_price.
 * Capped by tier / max_order_qty, rounded down to step_size.
 */
export function computeMaxOpenQtyBtc(maxNotionalUsdt, markPrice, {
    leverageTiers = [],
    leverage = 1,
    maxOrderQty,
    stepSize,
} = {}) {
    const notional = Number(maxNotionalUsdt);
    const p = Number(markPrice);
    const lev = Math.max(1, Number(leverage) || 1);
    if (!Number.isFinite(notional) || notional <= 0 || !Number.isFinite(p) || p <= 0) return 0;

    let maxQty = notional / p;

    const tierCap = getMaxQuantityAtLeverage(leverageTiers, lev, p);
    if (tierCap != null && Number.isFinite(tierCap) && tierCap > 0) {
        maxQty = Math.min(maxQty, tierCap);
    }

    const orderCap = Number(maxOrderQty);
    if (Number.isFinite(orderCap) && orderCap > 0) {
        maxQty = Math.min(maxQty, orderCap);
    }

    return roundQtyDownToStep(maxQty, stepSize);
}

/** @deprecated alias — prefer computeMaxOpenNotional + computeMaxOpenQtyBtc */
export function computeMaxOpenQuantity({
    effectiveAvailable,
    price,
    leverage,
    takerFeeRate = 0,
    leverageTiers = [],
    maxOrderQty,
    stepSize,
}) {
    const notional = computeMaxOpenNotional(effectiveAvailable, leverage, takerFeeRate);
    return computeMaxOpenQtyBtc(notional, price, { leverageTiers, leverage, maxOrderQty, stepSize });
}

/**
 * Futures leverage modal stats derived from available balance + tier caps.
 */
export function computeFuturesLeverageStats({
    availableBalance = 0,
    leverage = 1,
    maxLeverage = 125,
    leverageTiers = [],
}) {
    const bal = Number(availableBalance) || 0;
    const lev = Number(leverage) || 1;
    const maxLev = Number(maxLeverage) || 125;

    const maxNotionalAtLev = getMaxNotionalAtLeverage(leverageTiers, lev);
    const maxNotionalAtMaxLev = getMaxNotionalAtLeverage(leverageTiers, maxLev);

    const allowFromBalance = bal * lev;
    const allowToOpen =
        Number.isFinite(maxNotionalAtLev) && maxNotionalAtLev !== Infinity
            ? Math.min(allowFromBalance, maxNotionalAtLev)
            : allowFromBalance;

    const maxBorrowableFromBal = bal * Math.max(0, maxLev - 1);
    const maximumBorrowable =
        Number.isFinite(maxNotionalAtMaxLev) && maxNotionalAtMaxLev !== Infinity
            ? Math.min(maxBorrowableFromBal, maxNotionalAtMaxLev)
            : maxBorrowableFromBal;

    const currentLoanLimit =
        Number.isFinite(maxNotionalAtLev) && maxNotionalAtLev !== Infinity ? maxNotionalAtLev : null;

    return {
        allowToOpen,
        maximumBorrowable,
        maxLeverage: maxLev,
        currentLoanLimit,
        maxNotionalAtLev,
    };
}
